import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { WorkflowPropertyOption } from '../../../models/workflow-variable-catalog';

/**
 * Combobox de propiedad del objeto.
 *
 * Sustituye al `<input type="text">` a ciegas donde el usuario tenía que ADIVINAR el nombre de la
 * propiedad. Muestra las propiedades conocidas del objeto con etiqueta legible ("Correo (email)")
 * y una opción "Otra propiedad…" que revela un input libre para atributos personalizados o claves
 * no listadas.
 *
 * Contrato: emite SIEMPRE la CLAVE de la propiedad (string) por `valueChange`, exactamente igual
 * que el input de texto que reemplaza → no cambia lo que se guarda ni cómo lo interpreta el
 * backend. Si no hay opciones (objectType sin objeto base mapeado), cae a input de texto libre.
 */
@Component({
  selector: 'app-wf-property-select',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (options().length === 0) {
      <input type="text" [class]="fieldClass" [value]="value()" [disabled]="disabled()"
             [placeholder]="placeholder()" (input)="valueChange.emit(text($event))" />
    } @else if (custom()) {
      <div class="flex items-center gap-2">
        <input type="text" [class]="fieldClass" [value]="value()" [disabled]="disabled()"
               [placeholder]="placeholder()" (input)="onCustomInput($event)" />
        <button type="button" [disabled]="disabled()" (click)="backToList()" title="Elegir de la lista"
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:text-brand-500 disabled:opacity-40 dark:border-gray-700 dark:text-gray-400">
          <i class="fa-solid fa-list text-xs"></i>
        </button>
      </div>
    } @else {
      <select [class]="fieldClass" [value]="selectValue()" [disabled]="disabled()" (change)="onSelect($event)">
        <option value="">{{ placeholder() }}</option>
        @for (o of options(); track o.key) {
          <option [value]="o.key">{{ o.label }} ({{ o.key }})</option>
        }
        <option value="__custom__">Otra propiedad…</option>
      </select>
    }
  `
})
export class WorkflowPropertySelectComponent {
  readonly value = input<string>('');
  readonly options = input<WorkflowPropertyOption[]>([]);
  readonly disabled = input<boolean>(false);
  readonly placeholder = input<string>('Elige una propiedad…');
  readonly valueChange = output<string>();

  /** El usuario pidió explícitamente escribir una propiedad libre ("Otra propiedad…"). */
  private readonly manualCustom = signal(false);

  protected readonly fieldClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

  private readonly isKnown = computed(() => this.options().some((o) => o.key === this.value()));
  /** Modo texto libre: el usuario lo pidió, o el valor actual no está entre las opciones conocidas. */
  protected readonly custom = computed(() => this.manualCustom() || (this.value() !== '' && !this.isKnown()));
  protected readonly selectValue = computed(() => (this.isKnown() ? this.value() : ''));

  protected onSelect(event: Event): void {
    const selected = (event.target as HTMLSelectElement).value;
    if (selected === '__custom__') {
      this.manualCustom.set(true);
      return;
    }
    this.manualCustom.set(false);
    this.valueChange.emit(selected);
  }

  /**
   * Texto libre confirmado EN VIVO (evento `input`, no `change`): así el valor llega al store
   * aunque el usuario nunca saque el foco del campo.
   *
   * Se fija `manualCustom` en cada pulsación porque `custom()` también depende de si el valor
   * está entre las opciones conocidas: al teclear una clave que coincide con una del catálogo,
   * el bloque se cambiaría por el `<select>` a media escritura y el campo perdería el foco.
   */
  protected onCustomInput(event: Event): void {
    this.manualCustom.set(true);
    this.valueChange.emit(this.text(event));
  }

  protected backToList(): void {
    this.manualCustom.set(false);
    this.valueChange.emit('');
  }

  protected text(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}
